import styles from "./SurveyScreen.module.scss";

import { useCallback, useState } from "react";

import { useQuery } from "@tanstack/react-query";

import { SessionSurveyProvider } from "@/modules/session-survey/context/SessionSurveyProvider";
import { surveyDetailsQuery } from "@/modules/session-survey/options/surveyOptions";
import { SurveyAlreadyCompleted } from "@/modules/session-survey/screens/SurveyAlreadyCompleted";
import { SurveyClosed } from "@/modules/session-survey/screens/SurveyClosed";
import { SurveyUnavailable } from "@/modules/session-survey/screens/SurveyUnavailable";
import { getSurveyDetails } from "@/modules/session-survey/services/getSurveyDetails";
import { submitSurveyResponses } from "@/modules/session-survey/services/submitSurveyResponses";
import { AppLoadingIndicator } from "@/modules/shared/components/AppLoadingIndicator/AppLoadingIndicator";
import { ApiError } from "@/modules/shared/utils/ApiError";

import { SurveyQuestionFlow } from "./SurveyQuestionFlow";
import { SurveyStatusShell } from "./SurveyStatusShell";

interface SurveyScreenProps {
  surveyId: string;
  loadDetails?: typeof getSurveyDetails;
  submitResponses?: typeof submitSurveyResponses;
}

const isClosedError = (error: Error | null) =>
  error instanceof ApiError && (error.status === 404 || error.status === 410);

export const SurveyScreen = ({
  surveyId,
  loadDetails = getSurveyDetails,
  submitResponses,
}: SurveyScreenProps) => {
  const [isRetrying, setIsRetrying] = useState(false);
  const {
    data: details,
    error,
    isError,
    isLoading,
    refetch,
  } = useQuery(surveyDetailsQuery(surveyId, loadDetails));
  const handleRetry = useCallback(() => {
    setIsRetrying(true);
    void refetch().finally(() => setIsRetrying(false));
  }, [refetch]);

  if (isLoading && !isRetrying) {
    return (
      <SurveyStatusShell>
        <AppLoadingIndicator size="medium" />
      </SurveyStatusShell>
    );
  }

  if (isError && isClosedError(error)) {
    return (
      <SurveyStatusShell>
        <SurveyClosed />
      </SurveyStatusShell>
    );
  }

  if (isError || !details) {
    return (
      <SurveyStatusShell>
        <SurveyUnavailable onRetry={handleRetry} isRetrying={isRetrying} />
      </SurveyStatusShell>
    );
  }

  if (details.status === "completed") {
    return (
      <SurveyStatusShell>
        <SurveyAlreadyCompleted />
      </SurveyStatusShell>
    );
  }

  if (
    details.status === "expired" ||
    details.definition.questions.length === 0
  ) {
    return (
      <SurveyStatusShell>
        <SurveyClosed />
      </SurveyStatusShell>
    );
  }

  return (
    <div className={styles.container}>
      <SessionSurveyProvider
        details={details}
        submitResponses={submitResponses}
      >
        <SurveyQuestionFlow />
      </SessionSurveyProvider>
    </div>
  );
};
